import { Directive, ElementRef, HostListener, Input, OnChanges, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CurrencyPipe } from '@angular/common';
import { asNumber, isNull } from '../../shared';
import { isNil } from 'lodash-es';

const DEFAULT_SCALE = 7;
const DEFAULT_MAX = 1000000000;
type ValueType = number | string;

@Directive({
  selector: '[appCurrencyInput]',
  exportAs: 'appCurrencyInput',
})
export class CurrencyInputDirective implements OnChanges, OnInit {
  @Input()
  public min: number = null;

  @Input()
  public max: number = DEFAULT_MAX;

  @Input()
  public scale: number = DEFAULT_SCALE;

  @Input()
  public maxScale: number = null;

  @Input()
  public log: boolean = false;

  @Input()
  public allowNegative: boolean = false;

  @Input()
  specialValueToken: string | null = null;

  @Input()
  selectAllWhenFocus: boolean = false;

  @Input('appCurrencyInput')
  public set value(value: ValueType) {
    setTimeout(() => this.refreshView(value));
  }

  @Input()
  blankIfNull: boolean = false;

  @Input()
  suppressDecimals: boolean = false;

  private readonly value$: BehaviorSubject<ValueType> = new BehaviorSubject<ValueType>(null);

  @HostListener('focus', ['$event'])
  public onFocus = (): void => {
    const { ref, selectAllWhenFocus } = this;
    const elem: HTMLInputElement = ref.nativeElement;
    const ceilValue = elem.value.split('.')[0];

    elem.selectionStart = 0;
    elem.selectionEnd = selectAllWhenFocus ? elem.value.length : ceilValue.length;
  };

  @HostListener('input', ['$event'])
  public onInput = (): void => {
    const { ref, value$, min, max, scale, maxScale, allowNegative, specialValueToken, getFormattedValue } = this;
    const elem = ref.nativeElement;
    const str = elem.value;
    const caretPosition = elem.selectionStart;
    const splittedByDot = str.split('.');
    const formatMatch = allowNegative ? /^(-)?[\d,]*(.[\d]*)?$/ : /^[\d,]*(.[\d]*)?$/;
    const useScale = maxScale || scale;
    const raw = elem.value.trim();

    // Special symbol logic
    if (specialValueToken && raw.toUpperCase() === specialValueToken.toUpperCase()) {
      value$.next(specialValueToken.toUpperCase()); // Emit 'X' as string
      return;
    }

    // Disallow embedded special symbol like "X123" or "12X"
    if (specialValueToken && raw.toUpperCase().includes(specialValueToken.toUpperCase())) {
      elem.value = '';
      return;
    }

    const isBadFormat = (): boolean => {
      if (!formatMatch.test(str) || /,,/.test(str) || splittedByDot.length > 2) {
        return true;
      } else if (splittedByDot.length === 2) {
        const fracture = splittedByDot[1];
        return fracture.length > useScale || /,/.test(fracture);
      } else {
        return false;
      }
    };

    const applyChange = (val: number): void => {
      value$.next(asNumber(val.toFixed(useScale)));
    };

    const discardChange = (nextVal: number = null): void => {
      !isNull(nextVal) && applyChange(nextVal);
      elem.value = getFormattedValue();
      elem.setSelectionRange(caretPosition, caretPosition);
    };

    if (isBadFormat()) {
      discardChange();
    } else {
      const value = asNumber(str);

      if (!isNull(min) && value < min) {
        discardChange(min);
      } else if (!isNull(max) && value > max) {
        discardChange(max);
      } else {
        applyChange(value);
      }
    }
  };

  @HostListener('change', ['$event'])
  public onChange = () => {
    const { ref, getFormattedValue } = this;
    const elem = ref.nativeElement;

    elem.value = getFormattedValue();
  };

  public getFormattedValue = (): ValueType => {
    const { currencyPipe, value$, scale, maxScale, isTokenMatch, blankIfNull, suppressDecimals } = this;
    const { value } = value$;

    if (isTokenMatch(value)) {
      return value;
    }

    if (isNull(value) && blankIfNull) {
      return '';
    }

    const numericValue = asNumber(value);
    const hasDecimal = numericValue % 1 !== 0;

    if (suppressDecimals && !hasDecimal) {
      return numericValue.toString();
    }

    return currencyPipe.transform(numericValue, '', '', `1.${scale}-${maxScale || scale}`);
  };

  public getValue = () => {
    return this.value$.value;
  };

  public refreshView = (value: ValueType, forceUpdate: boolean = false): void => {
    const { ref, value$, scale, maxScale, getFormattedValue } = this;
    const useScale = maxScale || scale;
    const nextValue = this.isTokenMatch(value) || isNil(value) ? value : asNumber((+value || 0).toFixed(useScale));

    if (forceUpdate || `${nextValue}` !== `${value$.value}`) {
      value$.next(nextValue);
      ref.nativeElement.value = getFormattedValue();
    }
  };

  private isTokenMatch = (value: ValueType): boolean => {
    return typeof value === 'string' && value?.toUpperCase() === this.specialValueToken?.toUpperCase();
  };

  constructor(private currencyPipe: CurrencyPipe, private ref: ElementRef) {}

  ngOnInit(): void {
    this.refreshView(this.value$.value, true);
  }

  ngOnChanges(): void {
    this.refreshView(this.value$.value);
  }
}
